/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

type Bar = {
  name: string;
  value: number;
  segments: {
    intent: string;
    value: number;
  }[];
};

type IntentBarChartProps = {
  intentMetrics: {
    total_count: string;
    high_intent_percentage: string;
    list: Bar[];
  };
  handleFilterApply: (segmentName: string, barName: string) => void;
};

const IntentBarChartCard = ({
  intentMetrics,
  handleFilterApply,
}: IntentBarChartProps) => {
  const {
    list = [],
    total_count: totalCount,
    high_intent_percentage: highIntentPercentage,
  } = intentMetrics || {};
  const [hoveredSegment, setHoveredSegment] = useState<{
    barIndex: number;
    segmentIndex: number;
  } | null>(null);

  const highest = useRef<Bar>();

  const handleSegmentClick = (segmentName: string, barName: string) => {
    handleFilterApply(segmentName, barName);
  };

  const getIntentColor = (intent: string) => {
    switch (intent) {
      case 'HIGH':
      case 'HIGH_INTENT':
        return 'bg-positive-green bg-opacity-40';
      case 'LOW':
      case 'LOW_INTENT':
      case 'NOT_INTERESTED':
        return 'bg-negative-red bg-opacity-40';
      case 'INTERESTED':
      case 'MODERATE':
        return 'bg-secondary-orange bg-opacity-40';
      case 'NEUTRAL':
      case 'NEUTRAL_INTENT':
        return 'bg-neutral-orange bg-opacity-40';
      default:
        return 'bg-white';
    }
  };

  const bars = [
    'New',
    'In Progress',
    'Not Qualified',
    'Lost',
    'Converted',
    'NA',
  ];

  const statusConstant = (category: string) => {
    switch (category) {
      case 'New':
        return 'NEW';
      case 'In Progress':
        return 'IN_PROGRESS';
      case 'Not Qualified':
        return 'NOT_QUALIFIED';
      case 'Converted':
        return 'CONVERTED';
      case 'Lost':
        return 'LOST';
      case 'NA':
        return 'NA';
      default:
        return '';
    }
  };

  useEffect(() => {
    if (list?.length) {
      const max = (list || []).reduce((prev, current) =>
        prev && prev.value > current.value ? prev : current,
      );
      highest.current = max;
    }
  }, [list]);

  return (
    <div className="rounded-lg bg-white px-2.5 py-1.5 flex flex-col gap-2 text-sm w-full min-w-[350px] lg:min-w-[400px] max-w-[450px]">
      <div className="flex items-center">
        <span className="font-bold">
          {totalCount || 0}{' '}
          <span className="text-xs font-normal">Contacts</span>
        </span>
        <span className="text-xs font-normal mx-1">|</span>
        <span className="font-bold">
          {Number.isFinite(Number(highIntentPercentage)) ? Math.round(Number(highIntentPercentage)).toFixed(0) : 0}%{' '}
          <span className="text-xs font-normal">High Intent</span>
        </span>
      </div>
      <div className="flex flex-col gap-1">
        {bars.map((barText, barIndex) => {
          const item = (list || []).filter(itm => itm.name === barText);
          if (item?.length) {
            const bar = item?.[0];
            return (
              <div key={bar.name} className="flex items-center gap-2">
                <div className="w-[72px] max-w-[72px] text-xs truncate leading-3.5">
                  {bar.name}
                </div>
                <div className="flex-1">
                  <div
                    className="bars flex h-4 border border-[#ededed] rounded-[1px]"
                    style={{
                      width: `${
                        (bar.value /
                          (highest.current
                            ? highest.current?.value
                            : parseInt(totalCount, 10))) *
                        100
                      }%`,
                    }}
                  >
                    {bar.segments.map((segment, segmentIndex) => (
                      <div
                        key={segmentIndex}
                        className={cn(
                          'h-full relative cursor-pointer transition-all duration-200',
                          getIntentColor(segment.intent),
                          hoveredSegment?.barIndex === barIndex &&
                            hoveredSegment?.segmentIndex === segmentIndex
                            ? 'opacity-1 scale-y-110'
                            : 'opacity-60 hover:opacity-80 hover:scale-y-110',
                          segmentIndex === 0
                            ? 'rounded-l-[1px]'
                            : segmentIndex === bar.segments.length - 1
                            ? 'rounded-r-[1px]'
                            : '',
                        )}
                        style={{
                          width: `${Number(
                            (segment.value / bar.value) * 100,
                          ).toFixed(1)}%`,
                        }}
                        onMouseEnter={() =>
                          setHoveredSegment({ barIndex, segmentIndex })
                        }
                        onMouseLeave={() => setHoveredSegment(null)}
                        onClick={() =>
                          handleSegmentClick(
                            segment.intent,
                            statusConstant(bar.name),
                          )
                        }
                      >
                        {hoveredSegment?.barIndex === barIndex &&
                          hoveredSegment?.segmentIndex === segmentIndex && (
                            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-background border px-2 py-1 rounded-md text-xs whitespace-nowrap shadow-sm bg-white !opacity-100">
                              {segment.value} (
                              {Number(
                                (segment.value / bar.value) * 100,
                              ).toFixed(0)}
                              %)
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
                <div
                  className="w-[40px] text-xs flex gap-1 items-start truncate"
                  title={`${
                    Math.round(
                      Number((bar.value / parseInt(totalCount, 10)) * 100),
                    ).toFixed(0) || `~`
                  }`}
                >
                  <span className="font-bold">{bar.value || 0}</span>
                  <span className="text-[8px] text-tag-text-three">
                    {Math.round(
                      Number((bar.value / parseInt(totalCount, 10)) * 100),
                    ).toFixed(0) || `~`}
                    %
                  </span>
                </div>
              </div>
            );
          }
          return (
            <div key={barText} className="flex items-center gap-2">
              <div className="w-[72px] max-w-[72px] text-xs truncate leading-3.5">
                {barText}
              </div>
              <div className="flex-1">
                <div
                  className="bars flex h-4 border border-[#ededed] rounded-[1px]"
                  style={{ width: `100%` }}
                />
              </div>
              <div
                className="w-[40px] text-xs flex gap-1 items-start truncate"
                title="0%"
              >
                <span className="font-bold">0</span>
                <span className="text-[8px] text-tag-text-three">~%</span>
              </div>
            </div>
          );
        })}
      </div>
      <style jsx>{`
        @keyframes grow {
          from {
            max-width: 0;
          }
          to {
            max-width: 400px;
          }
        }

        .bars {
          animation: grow 3s ease-out;
          transition: all 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default IntentBarChartCard;
